import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { hashKey, isCancelledError, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, feedbackConversationAuthorityFingerprint, isCurrentFeedbackConversationScope,
  isCurrentRecapScope, listCoachFeedback, listRecipientFeedback, normalizeFeedbackCollectionQuery,
  observeAuthTokenChanges } from '@/lib/api';
import { feedbackCollectionKey, isAcknowledgedFeedbackCollectionRefresh } from '@/lib/queryClient';
import { useScopedIdentityContext } from '@/features/coach-review/composerDrafts';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import type { CoachFeedbackCollectionQuery, CoachFeedbackCollectionResponse, FeedbackCollectionScope,
  RecipientFeedbackCollectionQuery, RecipientFeedbackCollectionResponse } from '@/lib/types';

/** Conversation/authoring callers still require their narrower capability. */
export function useCanonicalScope(view: 'coach' | 'recipient'): FeedbackCollectionScope | null {
  const context = useScopedIdentityContext();
  return useMemo(() => {
    if (!context) return null;
    const { actorId, clubId, teamId, seasonId } = context;
    const scope = { actorId, clubId, teamId, seasonId, view };
    return isCurrentFeedbackConversationScope(scope) ? scope : null;
  }, [context, view]);
}

/** Observe every departure, even when React batches A -> B -> A into one render.
 * This stores only a read-lifetime counter and the existing credential-free
 * authority fingerprint. The API still owns authorization and transport fences. */
function useCollectionIdentity(view: 'coach' | 'recipient') {
  const context = useScopedIdentityContext();
  const observer = useMemo(() => {
    let snapshot = { authority: feedbackConversationAuthorityFingerprint(), generation: 0 };
    const getSnapshot = () => {
      const authority = feedbackConversationAuthorityFingerprint();
      if (authority !== snapshot.authority) snapshot = { authority, generation: snapshot.generation + 1 };
      return snapshot;
    };
    return { getSnapshot, subscribe: (notify: () => void) => {
      const changed = () => { getSnapshot(); notify(); };
      const offAuth = useAuthStore.subscribe(changed);
      const offScope = useScopeStore.subscribe(changed);
      const offToken = observeAuthTokenChanges(changed);
      changed();
      return () => { offAuth(); offScope(); offToken(); };
    } };
  }, []);
  const snapshot = useSyncExternalStore(observer.subscribe, observer.getSnapshot, observer.getSnapshot);
  return useMemo(() => {
    const candidate = context ? { actorId: context.actorId, clubId: context.clubId,
      teamId: context.teamId, seasonId: context.seasonId, view } : null;
    const scope = candidate && isCurrentRecapScope(candidate) ? candidate : null;
    return { scope, current: () => !!scope && observer.getSnapshot() === snapshot && isCurrentRecapScope(scope) };
  }, [context, view, snapshot, observer]);
}

/** Q09 admits independently authorized recap recipients; it is not a child grant. */
export function useCanonicalCollectionScope(view: 'coach' | 'recipient'): FeedbackCollectionScope | null {
  return useCollectionIdentity(view).scope;
}

class CollectionReadRetired extends Error {
  constructor() { super('Collection changed. Refresh to load current content.'); }
}
function cursorRefreshRequired(error: unknown) {
  if (error instanceof CollectionReadRetired) return true;
  if (!(error instanceof ApiError) || ![400, 409].includes(error.status)) return false;
  const code = error.detail && typeof error.detail === 'object' && 'code' in error.detail ? error.detail.code : null;
  return code === 'collection_query_refresh_required' || code === 'collection_cursor_invalid';
}

export function useCanonicalFeedback(view: 'coach' | 'recipient',
  controls: CoachFeedbackCollectionQuery | RecipientFeedbackCollectionQuery = {}, enabled = true,
  refreshVersion = 0) {
  const identity = useCollectionIdentity(view);
  const { scope } = identity;
  const client = useQueryClient();
  const [acknowledgedRefresh, setAcknowledgedRefresh] = useState(0);
  const controlsKey = JSON.stringify(normalizeFeedbackCollectionQuery(view, controls));
  const read = useMemo(() => {
    const key = scope ? feedbackCollectionKey(scope, controls) : ['feedback-collections', 'inactive', view];
    const previous = client.getQueryCache().find({ queryKey: key, exact: true });
    return { key, previous, updates: previous?.state.dataUpdateCount ?? 0, errors: previous?.state.errorUpdateCount ?? 0,
      controls: { ...controls } };
    // Canonical normalized controls, rather than a caller's object identity,
    // define the query. refreshVersion is an explicit first-page refresh intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, controlsKey, enabled, refreshVersion, acknowledgedRefresh, client]);
  const [ready, setReady] = useState<typeof read | null>(null);
  const [failure, setFailure] = useState<{ read: typeof read; error: Error; previous: typeof read.previous; updates: number } | null>(null);
  const retiredRead = useRef<{ read: typeof read; previous: typeof read.previous; updates: number } | null>(null);
  const active = identity.current();
  const query = useQuery<CoachFeedbackCollectionResponse | RecipientFeedbackCollectionResponse>({
    queryKey: read.key,
    queryFn: async ({ signal }) => {
      if (!scope || !enabled || !identity.current() || signal.aborted) throw new DOMException('Collection context changed', 'AbortError');
      retiredRead.current = null;
      setFailure(null);
      const executing = client.getQueryCache().find({ queryKey: read.key, exact: true });
      try {
        const value = view === 'coach'
          ? await listCoachFeedback(read.controls as CoachFeedbackCollectionQuery, { scope, signal })
          : await listRecipientFeedback(read.controls as RecipientFeedbackCollectionQuery, { scope, signal });
        if (!identity.current() || signal.aborted) throw new DOMException('Collection context changed', 'AbortError');
        return value;
      } catch (error) {
        // The existing transport removes/cancels the collection cache before
        // throwing an authorized denial/conflict. Preserve that typed error
        // even though its cache removal also aborts the query signal.
        if (identity.current() && (!signal.aborted || error instanceof ApiError)) {
          retiredRead.current = { read, previous: executing, updates: executing?.state.dataUpdateCount ?? 0 };
          setFailure({ read, error: error instanceof Error ? error : new Error('Collection read failed'),
            previous: executing, updates: executing?.state.dataUpdateCount ?? 0 });
        }
        throw error;
      }
    },
    // The transport can remove this query while delivering a denial. Drive
    // one read per captured lifetime/intent below, so cache removal cannot
    // silently restart a rejected cursor. Existing refetch remains explicit.
    enabled: false, retry: false, staleTime: 0,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (!enabled || !identity.current()) return;
    let disposed = false;
    // Q09 denial/refresh retires every page in this actor/view, including
    // sibling summary reads. Surface explicit recovery rather than leaving
    // their removed query silently loading or automatically replaying it.
    const off = client.getQueryCache().subscribe(event => {
      if (event.type === 'removed' && event.query.queryHash === hashKey(read.key)
        && !disposed && identity.current()) {
        const retired = retiredRead.current;
        // Another same-key observer can prove recovery without running this
        // observer's query function. Match the same success-counter rule used
        // for its rendered error below; an old local retirement cannot outlive it.
        const recovered = retired && (event.query !== retired.previous
          ? event.query.state.dataUpdateCount > 0 : event.query.state.dataUpdateCount > retired.updates);
        if (isAcknowledgedFeedbackCollectionRefresh(event.query) && read.controls.cursor == null
          && (retired?.read !== read || recovered) && (!event.query.state.error || isCancelledError(event.query.state.error))) {
          // Successful writes invalidate the snapshot, not this reader's
          // authority. Start one new cursorless read intent and hide old data
          // until it succeeds. Failed/retired reads keep explicit recovery.
          setAcknowledgedRefresh(value => value + 1);
          return;
        }
        retiredRead.current = { read, previous: event.query, updates: event.query.state.dataUpdateCount };
        setFailure({ read, error: new CollectionReadRetired(), previous: event.query, updates: event.query.state.dataUpdateCount });
      }
    });
    // A prior lifetime may still own this exact cache key after A -> B -> A.
    // Cancel it before starting a fresh read; never inherit its in-flight result.
    void client.cancelQueries({ queryKey: read.key, exact: true }, { revert: false }).then(() => {
      if (!disposed && identity.current()) {
        setReady(read);
        void query.refetch();
      }
    });
    return () => { disposed = true; off(); };
    // QueryObserver's refetch is stable; the captured read defines the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, identity, read, enabled]);
  const current = client.getQueryCache().find({ queryKey: read.key, exact: true });
  const fresh = !!current && (current !== read.previous
    ? current.state.dataUpdateCount > 0 : current.state.dataUpdateCount > read.updates);
  const admitted = enabled && active && ready === read;
  const freshError = !!current && (current !== read.previous
    ? current.state.errorUpdateCount > 0 : current.state.errorUpdateCount > read.errors);
  // A retry may be executed by another observer of the same shared key.
  // A newer successful result retires this observer's earlier local error too.
  const recovered = !!failure && !!current && (current !== failure.previous
    ? current.state.dataUpdateCount > 0 : current.state.dataUpdateCount > failure.updates);
  const error = admitted ? failure?.read === read && !recovered ? failure.error : freshError ? query.error : null : null;
  const data = admitted && fresh && !error ? query.data : undefined;
  return { ...query, data, error, isError: !!error, isSuccess: !!data,
    isLoading: enabled && active && !data && !error,
    scope, active, refreshRequired: cursorRefreshRequired(error) };
}
